// This is a shared modal used throughout for those actions that only require cancel/confirm

import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Modal from '../../../../../shared/_TBComponents/Modal';
import Button from '../../../../../shared/_TBComponents/Button';
import Text from '../../../../../shared/_TBComponents/Text';

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const CloseX = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 22px;
  line-height: 1;
  color: #9E9E9E;
  padding: 0 2px;
  flex-shrink: 0;

  &:hover { color: #444444; }
  &:focus-visible { outline: 2px solid #1787FB; outline-offset: 2px; }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const defaultLoadingLabel = (label) => (
  label.endsWith('e') ? `${label.slice(0, -1)}ing…` : `${label}ing…`
);

const ConfirmModal = ({
  isOpen, onClose, onConfirm, title, message, children, confirmLabel, loadingLabel,
  cancelLabel, isLoading, destructive, confirmIcon, hideConfirm,
}) => (
  <Modal
    isOpen={isOpen}
    onRequestClose={onClose}
    hideTitle
    hideButton
    customMaxWidth='440px'
    customMinHeight='0'
    customPadding='32px'
    customBorderRadius='8px'
  >
    <Body>
      <Header>
        <Text styledTextProps={{ fontSize: '20px', fontWeight: 800, color: '#333333' }}>
          {title}
        </Text>
        <CloseX type='button' onClick={onClose} aria-label='Close'>×</CloseX>
      </Header>
      <Text styledTextProps={{ fontSize: '14px', color: '#444444' }}>
        {message}
      </Text>
      {children}
      <Footer>
        <Button
          name={cancelLabel}
          bgColor='#FFF'
          borderColor='#EEE'
          txtColor='#444444'
          customBorderRadius='4px'
          fontSize='14px'
          styledTextProps={{ fontSize: '14px', fontWeight: 700 }}
          onClick={onClose}
        />
        {!hideConfirm && (
          <Button
            name={confirmLabel}
            loadingText={loadingLabel || defaultLoadingLabel(confirmLabel)}
            icon={isLoading ? null : confirmIcon}
            iconSize={14}
            iconColor='#FFF'
            bgColor={destructive ? '#FF0505' : '#1A1A1A'}
            borderColor={destructive ? '#FF0505' : '#1A1A1A'}
            txtColor='#FFF'
            customBorderRadius='4px'
            fontSize='14px'
            isLoading={isLoading}
            lightSpinner
            disabled={isLoading}
            styledTextProps={{ fontSize: '14px', fontWeight: 700 }}
            onClick={onConfirm}
          />
        )}
      </Footer>
    </Body>
  </Modal>
);

ConfirmModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  message: PropTypes.node.isRequired,
  children: PropTypes.node,
  confirmLabel: PropTypes.string,
  loadingLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  isLoading: PropTypes.bool,
  destructive: PropTypes.bool,
  confirmIcon: PropTypes.string,
  hideConfirm: PropTypes.bool,
};

ConfirmModal.defaultProps = {
  children: null,
  confirmLabel: 'Delete',
  loadingLabel: null,
  cancelLabel: 'Cancel',
  isLoading: false,
  destructive: true,
  confirmIcon: 'delete',
  hideConfirm: false,
};

export default ConfirmModal;
